import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { SCENE_KEYS } from './scene-keys.js';
import { dataManager, DATA_MANAGER_STORE_KEYS } from '../utils/data-manager.js';
import { netManager, realmForArea, REALM_LABELS } from '../net/index.js';
import { fetchPlayer } from '../net/api.js';
import { setGlobalSoundSettings, playBackgroundMusic } from '../utils/audio-utils.js';
import { SOUND_OPTIONS } from '../common/options.js';
import { AUDIO_ASSET_KEYS } from '../assets/asset-keys.js';

// ── Always-visible top-right HUD: Location + weather + live clock, plus the Gold counter. ──
// Runs as a parallel overlay on top of the world scene — it never pauses gameplay. The location
// name is derived from the player's current area (PLAYER_LOCATION) via the realm label table; the
// clock shows real local time. Gold reads from the api (`/player`) when connected, otherwise the
// local data-manager value. Premium glass: dark rounded panel, emerald + gold accents.

const EMERALD = 0x34d399;
const GOLD = 0xf5b23e;
const GLASS = 0x051610;
const RADIUS = 14;

const PADDING = 14;
const PANEL_WIDTH = 232;
const PANEL_HEIGHT = 116; // extra row at the bottom for the daily-streak multiplier badge
// Minimap radar (top-right, under the currency panel): a square showing you (center) + nearby
// players/bots as dots, within RADAR_RANGE world px. Tap it to open the full map.
const MINIMAP_SIZE = 132;
const RADAR_RANGE = 1500;

/** True on phones/tablets (coarse pointer or touch events). Drives the mobile HUD simplifications. */
function isTouchDevice() {
  return (
    (typeof window !== 'undefined' &&
      ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window)) ||
    false
  );
}

export class CurrencyHudScene extends Phaser.Scene {
  /** @type {Phaser.GameObjects.Container} minimap radar (you + nearby players) */
  #minimap;
  /** @type {Phaser.GameObjects.Graphics} the live radar dots, cleared + redrawn each frame */
  #radar;
  /** @type {Phaser.GameObjects.Container} */
  #container;
  /** @type {Phaser.GameObjects.Graphics} */
  #bg;
  /** @type {Phaser.GameObjects.Text} */
  #locationText;
  /** @type {Phaser.GameObjects.Text} */
  #clockText;
  /** @type {Phaser.GameObjects.Text} live online-players tally */
  #onlineText;
  /** @type {Phaser.GameObjects.Text} */
  #goldText;
  /** @type {Phaser.GameObjects.Text | undefined} daily-streak multiplier badge */
  #streakText;
  /** @type {Phaser.Time.TimerEvent} */
  #clockTimer;
  /** @type {Phaser.GameObjects.Container} top-center controls hint + music mute */
  #topBar;
  /** @type {Phaser.GameObjects.Text} */
  #muteBtn;

  constructor() {
    super({ key: SCENE_KEYS.CURRENCY_HUD_SCENE });
  }

  /** @returns {void} */
  create() {
    this.#bg = this.add.graphics();
    this.#bg.fillStyle(GLASS, 0.82);
    this.#bg.fillRoundedRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, RADIUS);
    this.#bg.lineStyle(2, EMERALD, 0.85);
    this.#bg.strokeRoundedRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, RADIUS);

    // location + weather (top row) — right-aligned to the panel
    const sun = this.add.text(14, 12, '☀', { fontFamily: 'system-ui, sans-serif', fontSize: '18px', color: '#f5b23e' });
    this.#locationText = this.add
      .text(PANEL_WIDTH - 14, 14, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '16px', color: '#eafff5' })
      .setOrigin(1, 0);

    // live clock (middle row, right)
    this.#clockText = this.add
      .text(PANEL_WIDTH - 14, 40, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '14px', color: '#aef0c4' })
      .setOrigin(1, 0);

    // live online-players tally (middle row, left) — updated every tick from the realtime room
    this.#onlineText = this.add.text(14, 40, '🟢 — online', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '13px',
      color: '#7be7a8',
    });

    // gold (bottom row)
    const coin = this.add.text(14, 64, '🪙', { fontFamily: 'system-ui, sans-serif', fontSize: '16px' });
    const goldLabel = this.add.text(40, 66, 'GOLD', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '14px',
      color: '#f5b23e',
    });
    this.#goldText = this.add
      .text(PANEL_WIDTH - 14, 64, `${dataManager.getGold()}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: '#ffd34d',
      })
      .setOrigin(1, 0);

    // daily-streak multiplier badge — its OWN row BELOW the gold (left-aligned), so it can never
    // overlap the right-aligned Gold number (a big balance used to render straight through it).
    this.#streakText = this.add
      .text(14, 90, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '14px', color: '#ff8a3d' })
      .setOrigin(0, 0)
      .setVisible(false);

    this.#container = this.add
      .container(0, 0, [this.#bg, sun, this.#locationText, this.#clockText, this.#onlineText, coin, goldLabel, this.#goldText, this.#streakText])
      .setScrollFactor(0)
      .setDepth(1000);

    this.#buildMinimap();
    this.#buildTopBar();

    this.#refreshLocation();
    this.#updateClock();
    this.#reposition();

    // tick the clock every second
    this.#clockTimer = this.time.addEvent({ delay: 1000, loop: true, callback: this.#updateClock, callbackScope: this });

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#reposition, this);
    dataManager.store.events.on(`changedata-${DATA_MANAGER_STORE_KEYS.GOLD}`, this.#onGoldChanged, this);
    dataManager.store.events.on(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION}`, this.#refreshLocation, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.#clockTimer?.remove(false);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#reposition, this);
      dataManager.store.events?.off?.(`changedata-${DATA_MANAGER_STORE_KEYS.GOLD}`, this.#onGoldChanged, this);
      dataManager.store.events?.off?.(`changedata-${DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION}`, this.#refreshLocation, this);
    });

    void this.#syncFromApi();
  }

  /**
   * Build the top-center bar. On touch devices the keyboard hint ("WASD … SPACE … Click") is
   * meaningless and overflows a phone, so we render ONLY a compact mute chip top-center. On desktop
   * we keep the full controls-hint bar with the mute button at its right. @returns {void}
   */
  #buildTopBar() {
    if (isTouchDevice()) {
      this.#buildMuteChip();
      return;
    }
    const BAR_W = 540;
    const BAR_H = 34;
    const bg = this.add.graphics();
    bg.fillStyle(GLASS, 0.82);
    bg.fillRoundedRect(0, 0, BAR_W, BAR_H, 10);
    bg.lineStyle(2, EMERALD, 0.7);
    bg.strokeRoundedRect(0, 0, BAR_W, BAR_H, 10);
    const hint = this.add
      .text(16, BAR_H / 2, 'WASD / Arrows  Move      SPACE  Interact      Click  Menus', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '13px',
        color: '#cdeede',
      })
      .setOrigin(0, 0.5);
    this.#muteBtn = this.add
      .text(BAR_W - 24, BAR_H / 2, this.#musicGlyph(), { fontFamily: 'system-ui, sans-serif', fontSize: '18px' })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.#muteBtn.on(Phaser.Input.Events.POINTER_DOWN, () => this.#toggleMusic());
    this.#topBar = this.add.container(0, 0, [bg, hint, this.#muteBtn]).setScrollFactor(0).setDepth(1000);
    this.#topBar.setSize(BAR_W, BAR_H);
  }

  /** Touch-only: a small standalone 🔊 chip (top-center), big enough to thumb-tap. @returns {void} */
  #buildMuteChip() {
    const CHIP = 38;
    const bg = this.add.graphics();
    bg.fillStyle(GLASS, 0.82);
    bg.fillRoundedRect(0, 0, CHIP, CHIP, 10);
    bg.lineStyle(2, EMERALD, 0.7);
    bg.strokeRoundedRect(0, 0, CHIP, CHIP, 10);
    this.#muteBtn = this.add
      .text(CHIP / 2, CHIP / 2, this.#musicGlyph(), { fontFamily: 'system-ui, sans-serif', fontSize: '20px' })
      .setOrigin(0.5);
    // The whole chip is the tap target (thumb-sized), not just the glyph.
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, CHIP, CHIP), Phaser.Geom.Rectangle.Contains);
    bg.on(Phaser.Input.Events.POINTER_DOWN, () => this.#toggleMusic());
    this.#topBar = this.add.container(0, 0, [bg, this.#muteBtn]).setScrollFactor(0).setDepth(1000);
    this.#topBar.setSize(CHIP, CHIP);
  }

  /** @returns {string} speaker glyph reflecting the current music on/off state */
  #musicGlyph() {
    return dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC) === SOUND_OPTIONS.OFF ? '🔇' : '🔊';
  }

  /** Toggle background music on/off (instant). @returns {void} */
  #toggleMusic() {
    const cur = dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC);
    const next = cur === SOUND_OPTIONS.OFF ? SOUND_OPTIONS.ON : SOUND_OPTIONS.OFF;
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC, next);
    setGlobalSoundSettings(this);
    if (next === SOUND_OPTIONS.ON) {
      playBackgroundMusic(this, AUDIO_ASSET_KEYS.MAIN);
    }
    this.#muteBtn?.setText(this.#musicGlyph());
  }

  /** @returns {void} */
  /** Build the minimap radar panel (a glass square). Tapping it opens the full Map scene. */
  #buildMinimap() {
    const S = MINIMAP_SIZE;
    const bg = this.add.graphics();
    bg.fillStyle(GLASS, 0.82);
    bg.fillRoundedRect(0, 0, S, S, RADIUS);
    bg.lineStyle(2, EMERALD, 0.7);
    bg.strokeRoundedRect(0, 0, S, S, RADIUS);
    const label = this.add.text(9, 6, '🗺️', { fontSize: '13px' });
    this.#radar = this.add.graphics();
    const hit = this.add.zone(0, 0, S, S).setOrigin(0).setInteractive({ useHandCursor: true });
    hit.on(Phaser.Input.Events.POINTER_DOWN, () => this.scene.get(SCENE_KEYS.WORLD_SCENE)?.openMap?.());
    this.#minimap = this.add.container(0, 0, [bg, this.#radar, label, hit]).setScrollFactor(0).setDepth(999);
  }

  /** Per-frame: redraw the radar from the world scene's snapshot (you centered + nearby players). */
  update() {
    if (!this.#radar || !this.#minimap) return;
    const snap = this.scene.get(SCENE_KEYS.WORLD_SCENE)?.getRadarSnapshot?.();
    const minDim = Math.min(this.scale.width, this.scale.height);
    if (!snap || minDim <= 420) {
      this.#minimap.setVisible(false);
      return;
    }
    this.#minimap.setVisible(true);
    const g = this.#radar;
    g.clear();
    const half = MINIMAP_SIZE / 2;
    const r = half - 12;
    // Other players + ambient bots — green dots, clamped to the radar edge if out of range.
    for (const o of snap.others) {
      let dx = ((o.x - snap.px) / RADAR_RANGE) * r;
      let dy = ((o.y - snap.py) / RADAR_RANGE) * r;
      const d = Math.hypot(dx, dy);
      if (d > r) {
        dx = (dx / d) * r;
        dy = (dy / d) * r;
      }
      g.fillStyle(0x9be86b, 0.9);
      g.fillCircle(half + dx, half + dy, 3);
    }
    // You — bright dot at the center.
    g.fillStyle(0x34d399, 1);
    g.fillCircle(half, half, 4.5);
    g.lineStyle(1.5, 0xffffff, 0.85);
    g.strokeCircle(half, half, 4.5);
  }

  #reposition() {
    const sw = this.scale.width;
    // Narrow phones: shrink the currency panel so it can't overflow or collide with the top-left
    // player card. Scaled about its TOP-RIGHT corner so it stays pinned to the right edge.
    const minDim = Math.min(sw, this.scale.height);
    const panelScale = minDim <= 380 ? 0.78 : minDim <= 480 ? 0.86 : 1;
    const CARD_RIGHT = PADDING + 264; // top-left player card right edge (world-hud, unscaled)
    const CARD_BOTTOM = PADDING + 96;
    let panelDropped = false;
    if (this.#container) {
      this.#container.setScale(panelScale);
      const panelW = PANEL_WIDTH * panelScale;
      const panelLeft = sw - panelW - PADDING;
      // If the scaled currency panel would horizontally overlap the player card on a narrow screen,
      // drop it just BELOW the card band so the two never collide (location/clock/🟢 online/gold
      // stay fully readable).
      panelDropped = panelLeft < CARD_RIGHT + 8;
      this.#container.setPosition(panelLeft, panelDropped ? CARD_BOTTOM + 8 : PADDING);
    }

    // Minimap radar: right-aligned just under the currency panel, scaled with it.
    if (this.#minimap) {
      this.#minimap.setScale(panelScale);
      const mmSize = MINIMAP_SIZE * panelScale;
      const panelTop = panelDropped ? CARD_BOTTOM + 8 : PADDING;
      this.#minimap.setPosition(sw - mmSize - PADDING, panelTop + PANEL_HEIGHT * panelScale + 8);
    }

    if (isTouchDevice()) {
      // Mute chip: top-RIGHT when the panel dropped below the card (the top-right corner is then
      // free), else top-center. Either way it clears the player card + currency panel.
      const CHIP = 38;
      const chipX = panelDropped ? sw - CHIP - PADDING : (sw - CHIP) / 2;
      this.#topBar?.setPosition(chipX, PADDING);
      return;
    }

    // Desktop: top-center controls bar (clamped so it never collides with the currency panel).
    const barW = 540;
    const maxRight = sw - PANEL_WIDTH - PADDING * 2;
    let x = (sw - barW) / 2;
    if (x + barW > maxRight) x = Math.max(PADDING, maxRight - barW);
    this.#topBar?.setPosition(x, PADDING);
  }

  /** Resolve the current area to a human-readable realm/town name. @returns {void} */
  #refreshLocation() {
    const location = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION);
    const realm = realmForArea(location?.area);
    this.#locationText?.setText(REALM_LABELS[realm] ?? 'The Wilds');
  }

  /** Render the live local time as "10:24 AM" + the live online-players tally. @returns {void} */
  #updateClock() {
    const now = new Date();
    const text = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    this.#clockText?.setText(text);
    const online = netManager.client?.onlineCount ?? 0;
    this.#onlineText?.setText(online > 0 ? `🟢 ${online} online` : '🟢 — online');
  }

  /**
   * @param {Phaser.Data.DataManager} _parent
   * @param {number} value
   * @returns {void}
   */
  #onGoldChanged(_parent, value) {
    this.#goldText?.setText(`${value}`);
  }

  /** @returns {Promise<void>} */
  async #syncFromApi() {
    const wallet = netManager.wallet;
    const player = await fetchPlayer(wallet);
    // the scene may have been shut down while the fetch was in flight — bail before touching state
    if (!this.sys.isActive()) {
      return;
    }
    if (player && typeof player.gold === 'number') {
      dataManager.setGold(player.gold);
    }
    this.#updateStreakBadge(player);
  }

  /** Always show the daily-streak Gold/XP multiplier so players can SEE + track it (1.0×→3.0×). At
   *  1.0× it reads as a hook to keep playing; higher streaks glow brighter. */
  #updateStreakBadge(player) {
    if (!this.#streakText) return;
    const mult = Number(player?.streakMultiplier) || 1;
    const maxed = mult >= 3;
    this.#streakText
      .setText(`🔥 ${mult.toFixed(1)}× gold${maxed ? ' MAX' : ''}`)
      .setColor(maxed ? '#ffd34d' : mult > 1 ? '#ff8a3d' : '#c9a76a')
      .setVisible(true);
  }
}
